import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SettingCard, Row } from "./adminShared";
import { Cpu, ShieldCheck, Info, Database, Palette, Upload, Loader2, Image, RotateCcw } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { BrandPreview } from "@/components/admin/BrandPreview";

const MODELS = ["claude-sonnet-4-6", "gpt-5.4", "gemini-3.1-pro-preview", "claude-opus-4-6"];

export default function AiSettings({ settings, saveSetting, saveBrand, resetBrand }) {
  const [uploading, setUploading] = useState(false);
  const [brandPrimary, setBrandPrimary] = useState(settings.brand_primary || "#10B981");
  const fileRef = useRef(null);
  const [heroUrls, setHeroUrls] = useState({});
  const [heroBusy, setHeroBusy] = useState(null);
  const [aiCfg, setAiCfg] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const heroRefs = { login_candidate: useRef(null), login_employer: useRef(null) };
  useEffect(() => { api.get("/org/branding/hero").then(({ data }) => setHeroUrls(data || {})).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/ai-config").then(({ data }) => setAiCfg(data)).catch(() => {}); }, []);
  const saveAiCfg = async (patch) => {
    const next = { provider: aiCfg.provider, model: aiCfg.model, ocr_enabled: aiCfg.ocr_enabled, ...patch };
    if (patch.provider && patch.provider !== aiCfg.provider) next.model = (aiCfg.options[patch.provider] || [])[0];
    setAiBusy(true);
    try { const { data } = await api.put("/admin/ai-config", next); setAiCfg(data); toast.success("CV reading engine updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setAiBusy(false); }
  };
  const uploadHero = async (slot, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setHeroBusy(slot);
    try {
      const fd = new FormData();
      fd.append("slot", slot);
      fd.append("file", file);
      const { data } = await api.post("/org/branding/hero", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHeroUrls((h) => ({ ...h, [slot]: data.url }));
      toast.success("Marketing image updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setHeroBusy(null);
      if (heroRefs[slot].current) heroRefs[slot].current.value = "";
    }
  };
  const removeHero = async (slot) => {
    try { await api.delete(`/org/branding/hero/${slot}`); setHeroUrls((h) => ({ ...h, [slot]: null })); toast.success("Reset to default image"); }
    catch { toast.error("Could not reset"); }
  };
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/org/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      saveBrand("brand_logo_url", data.brand_logo_url);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <div className="space-y-4 max-w-2xl" data-testid="admin-settings">
      <SettingCard icon={Cpu} title="AI model bindings" desc="Models used for the job builder and candidate scoring.">
        <Row label="Job builder model">
          <Select value={settings.ai_model} onValueChange={(v) => saveSetting("ai_model", v)}>
            <SelectTrigger className="w-56 h-9 font-mono text-xs" data-testid="binding-ai-model"><SelectValue /></SelectTrigger>
            <SelectContent>{MODELS.map((m) => <SelectItem key={m} value={m} className="font-mono text-xs">{m}</SelectItem>)}</SelectContent>
          </Select>
        </Row>
        <Row label="Scoring model">
          <Select value={settings.scoring_model} onValueChange={(v) => saveSetting("scoring_model", v)}>
            <SelectTrigger className="w-56 h-9 font-mono text-xs" data-testid="binding-scoring-model"><SelectValue /></SelectTrigger>
            <SelectContent>{MODELS.map((m) => <SelectItem key={m} value={m} className="font-mono text-xs">{m}</SelectItem>)}</SelectContent>
          </Select>
        </Row>
      </SettingCard>

      <SettingCard icon={ShieldCheck} title="Trust & compliance" desc="Guardrails applied across every assessment.">
        <Row label="Require human approval before decisions">
          <Switch checked={settings.human_approval_required} onCheckedChange={(v) => saveSetting("human_approval_required", v)} data-testid="toggle-human-approval" />
        </Row>
        <Row label="Proctoring enabled">
          <Switch checked={settings.proctoring_enabled} onCheckedChange={(v) => saveSetting("proctoring_enabled", v)} data-testid="toggle-proctoring" />
        </Row>
        <Row label="Proctoring mode">
          <Select value={settings.proctoring_mode} onValueChange={(v) => saveSetting("proctoring_mode", v)}>
            <SelectTrigger className="w-40 h-9 text-xs" data-testid="proctoring-mode"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="advisory">Advisory only</SelectItem><SelectItem value="off">Off</SelectItem></SelectContent>
          </Select>
        </Row>
        <Row label="Allow alternative non-biometric path">
          <Switch checked={settings.allow_alternative_path} onCheckedChange={(v) => saveSetting("allow_alternative_path", v)} data-testid="toggle-alternative-path" />
        </Row>
        <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 flex gap-2.5 mt-1">
          <Info className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
          <p className="text-xs text-blue-200">Proctoring is advisory-only by policy. Identity signals must never be presented as proof of misconduct.</p>
        </div>
      </SettingCard>

      <SettingCard icon={Database} title="Data retention" desc="How long candidate data is retained before automatic deletion.">
        <div className="space-y-3">
          <div className="flex justify-between text-sm"><span>Retention period</span><span className="font-mono text-primary">{settings.data_retention_days} days</span></div>
          <Slider value={[settings.data_retention_days]} min={30} max={365} step={30} onValueCommit={(v) => saveSetting("data_retention_days", v[0])} data-testid="retention-slider" />
        </div>
      </SettingCard>

      <SettingCard icon={Palette} title="Brand & white-label" desc="Your logo, colours and name — applied live across this tenant's workspace.">
        <div className="space-y-3">
          <BrandPreview primary={brandPrimary} name={settings.brand_name} logo={settings.brand_logo_url} />
          <Row label="Brand name">
            <input defaultValue={settings.brand_name || "HireStream"} onBlur={(e) => saveBrand("brand_name", e.target.value)} data-testid="brand-name-input" className="w-56 h-9 px-3 rounded-md bg-muted/50 border border-border/60 text-sm outline-none focus:border-primary" />
          </Row>
          <Row label="Logo">
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2">
                {settings.brand_logo_url ? <img src={settings.brand_logo_url} alt="logo" className="h-9 w-9 rounded-md object-cover border border-border/60" data-testid="brand-logo-preview" /> : null}
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={uploadLogo} className="hidden" data-testid="brand-logo-file" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="brand-logo-upload" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-muted/50 border border-border/60 text-sm hover:border-primary disabled:opacity-60">
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
                </button>
              </div>
              <input defaultValue={settings.brand_logo_url || ""} placeholder="…or paste an image URL" onBlur={(e) => saveBrand("brand_logo_url", e.target.value)} data-testid="brand-logo-input" className="w-56 h-8 px-3 rounded-md bg-muted/50 border border-border/60 text-xs outline-none focus:border-primary" />
            </div>
          </Row>
          <Row label="Primary colour">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-muted-foreground">{settings.brand_primary || "#10B981"}</span>
              <input type="color" defaultValue={settings.brand_primary || "#10B981"} onChange={(e) => { setBrandPrimary(e.target.value); saveBrand("brand_primary", e.target.value); }} data-testid="brand-primary-input" className="h-9 w-14 rounded-md bg-transparent border border-border/60 cursor-pointer" />
            </div>
          </Row>
          <Row label="Accent colour">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-muted-foreground">{settings.brand_accent || "#22C881"}</span>
              <input type="color" defaultValue={settings.brand_accent || "#22C881"} onChange={(e) => saveBrand("brand_accent", e.target.value)} data-testid="brand-accent-input" className="h-9 w-14 rounded-md bg-transparent border border-border/60 cursor-pointer" />
            </div>
          </Row>
          <div className="pt-1">
            <button type="button" onClick={resetBrand} data-testid="brand-reset" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <RotateCcw className="h-3.5 w-3.5" /> Reset to HireStream default
            </button>
          </div>
        </div>
      </SettingCard>

      <SettingCard icon={Cpu} title="CV reading engine" desc="Choose the AI model that reads & parses candidate CVs, and whether scanned/photo CVs are read with vision OCR.">
        {aiCfg ? (
          <div className="space-y-3" data-testid="cv-ai-config">
            <Row label="Provider">
              <select value={aiCfg.provider} onChange={(e) => saveAiCfg({ provider: e.target.value })} disabled={aiBusy} data-testid="cv-provider" className="rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-primary">
                {Object.keys(aiCfg.options || {}).map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Row>
            <Row label="Model">
              <select value={aiCfg.model} onChange={(e) => saveAiCfg({ model: e.target.value })} disabled={aiBusy} data-testid="cv-model" className="rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-primary">
                {(aiCfg.options?.[aiCfg.provider] || []).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </Row>
            <Row label="Vision OCR for scanned CVs">
              <button onClick={() => saveAiCfg({ ocr_enabled: !aiCfg.ocr_enabled })} disabled={aiBusy} data-testid="cv-ocr-toggle" className={`relative h-6 w-11 rounded-full transition-colors ${aiCfg.ocr_enabled ? "bg-primary" : "bg-muted"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${aiCfg.ocr_enabled ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </Row>
          </div>
        ) : <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
      </SettingCard>

      <SettingCard icon={Image} title="Marketing images" desc="Photos shown on the public sign-in page. Uploads apply site-wide; falls back to the defaults when unset.">
        <div className="space-y-3" data-testid="hero-images">
          {[["login_candidate", "Candidate hero (login)"], ["login_employer", "Employer hero (login)"], ["landing_hiring", "Landing — hiring path"], ["landing_candidate", "Landing — candidate path"]].map(([slot, label]) => (
            <Row key={slot} label={label}>
              <div className="flex items-center gap-2">
                {heroUrls[slot] ? <img src={heroUrls[slot]} alt={label} loading="lazy" className="h-9 w-16 rounded-md object-cover border border-border/60" data-testid={`hero-preview-${slot}`} /> : <span className="text-xs text-muted-foreground">Default</span>}
                <input ref={heroRefs[slot]} type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => uploadHero(slot, e)} className="hidden" data-testid={`hero-file-${slot}`} />
                <button type="button" onClick={() => heroRefs[slot].current?.click()} disabled={heroBusy === slot} data-testid={`hero-upload-${slot}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-muted/50 border border-border/60 text-sm hover:border-primary disabled:opacity-60">
                  {heroBusy === slot ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
                </button>
                {heroUrls[slot] && <button type="button" onClick={() => removeHero(slot)} data-testid={`hero-remove-${slot}`} className="text-xs text-muted-foreground hover:text-destructive-foreground">Reset</button>}
              </div>
            </Row>
          ))}
        </div>
      </SettingCard>
    </div>
  );
}
